import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  CircularProgress,
} from '@mui/material';
import { useState, useEffect } from 'react';
import TextField from '@/components/textField';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  initialName: string;
  initialDescription: string;
  loading: boolean;
  onCancel: () => void;
  onSave: (name: string, description: string) => void;
}

const SavePipelineDialog = ({ open, initialName, initialDescription, loading, onCancel, onSave }: Props) => {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setName(initialName);
      setDescription(initialDescription);
      setError('');
    }
  }, [open, initialName, initialDescription]);

  const handleSave = () => {
    if (!name.trim()) {
      setError(t('common.errors.requiredField', 'Required field'));
      return;
    }
    setError('');
    onSave(name, description);
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Save Pipeline</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Provide a name and description for this pipeline to save it for later use.
        </DialogContentText>
        <Stack spacing={2} sx={{ mt: 1.5 }}>
          <TextField
            autoFocus
            label="Pipeline Name"
            value={name}
            maxLength={100}
            onChange={(value) => {
              setName(value);
              if (value.trim()) {
                setError('');
              }
            }}
            error={error}
            required
            disabled={loading}
            placeholder="e.g., IoT Telemetry sync"
          />
          <TextField
            label="Description"
            multiline
            rows={3}
            value={description}
            onChange={(value) => setDescription(value)}
            disabled={loading}
            placeholder="Describe what data flows through this pipeline"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          {t('common.buttons.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {loading ? 'Saving...' : t('common.buttons.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SavePipelineDialog;
